/**
 * The follower's step (D238) — who goes with the player through a doorway,
 * who stops at it, and who is found waiting on the other side
 * (content-schema `actor-follows.ts` has the authored shape).
 *
 * @remarks
 * Read at the one seam every arrival passes through — `enterRoom` — so a
 * `go`, a `move_player`, a lift ride and anything else that puts the player
 * in a room carry the company alike. Three rules, each the reason for a line
 * below:
 *
 * - **Judged in the doorway.** A follower's `while`, a refusal's `when`, and
 *   where every body stands are read from the state as the player LEAVES:
 *   the company is the bodies in the room being left, which is exactly the
 *   question a trigger could never ask, because a trigger sees the world
 *   after the step.
 * - **Nothing is remembered.** A follower follows from wherever the player
 *   finds it. There is no "following" flag to set or clear — a body the
 *   player leaves behind (a refusal, a `move_actor` sending it ahead, a
 *   `while` gone false) is followed again the moment the player stands with
 *   it and `while` holds. So the rejoin is not a state change either, only a
 *   line the pack may author for the moment it is found.
 * - **Only an arrival from somewhere else.** Session start and the
 *   checkpoint's return put the player in a room they are already recorded
 *   in, so nothing is left and nothing is found: the photograph already says
 *   where every body stands.
 *
 * @packageDocumentation
 */

import type { ActorDefinition, ActorFollows, FollowRefusal, NarrativeLine, RoomId, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { type StampedNarratives, stampNarrativeLines, type TranscriptCursor } from '../execution/transcript-cursor.js';
import { actorMovedProvenance, withActorLocation } from './actor-movement.js';
import { isActorVisible, roomOfActor } from './actor-presence.js';

/** An actor that declares `follows` — the only bodies a step through a doorway reads. */
export type Follower = { readonly follows: ActorFollows } & ActorDefinition;

/** A follower that stops at the threshold, and the arm that stopped it. */
export interface HoldingFollower {
	readonly actor: Follower;
	readonly refusal: FollowRefusal;
}

/** What one step through a doorway does to the company the player keeps. */
export interface FollowingStep {
	/** The followers that come through with the player — moved, silently. */
	readonly arriving: readonly Follower[];
	/** The followers that stop at the threshold, in their refusal's words. */
	readonly holding: readonly HoldingFollower[];
	/** The followers already standing in the room entered — found again. */
	readonly waiting: readonly Follower[];
}

/** No company moves: the player did not leave anywhere. */
const STANDING_STILL: FollowingStep = { arriving: [], holding: [], waiting: []};

/**
 * Is this actor a follower?
 *
 * @param actor - The actor
 * @returns Whether it declares `follows`
 */
const isFollower = (actor: ActorDefinition): actor is Follower => actor.follows !== undefined;

/**
 * Does this follower go where the player goes right now?
 *
 * @param follows - Its declaration
 * @param bundle - The loaded bundle
 * @param state - The state in the doorway
 * @returns Whether its `while` holds (absent: always)
 */
const isFollowing = (follows: ActorFollows, bundle: EngineGameBundle, state: RuntimeState): boolean =>
	follows.while === undefined || evaluateCondition(follows.while, bundle, state);

/**
 * The first refusal arm that keeps this follower out of the room — first
 * match wins, an arm without `when` always holds.
 *
 * @param follows - Its declaration
 * @param targetRoomId - The room the player enters
 * @param bundle - The loaded bundle
 * @param state - The state in the doorway
 * @returns The arm, or `undefined` when it comes through
 */
const refusalOf = (follows: ActorFollows, targetRoomId: RoomId, bundle: EngineGameBundle, state: RuntimeState): FollowRefusal | undefined =>
	follows.refuses.find((refusal) => refusal.roomIds.includes(targetRoomId) && (refusal.when === undefined || evaluateCondition(refusal.when, bundle, state)));

/**
 * Read one step through a doorway: of every follower whose `while` holds, in
 * the pack's declared order, those in the room being left arrive or hold,
 * and those in the room entered are waiting.
 *
 * @param bundle - The loaded bundle
 * @param state - The state in the doorway — the player still recorded in the room they leave
 * @param targetRoomId - The room the player enters
 * @returns The step — nothing at all when the player is not leaving anywhere
 */
export const followingStepOf = (bundle: EngineGameBundle, state: RuntimeState, targetRoomId: RoomId): FollowingStep => {
	const leftRoomId = state.progression.activeRoomId;

	if (leftRoomId === targetRoomId) {
		return STANDING_STILL;
	}

	const arriving: Follower[] = [];
	const holding: HoldingFollower[] = [];
	const waiting: Follower[] = [];

	for (const actor of bundle.contentPack.actors.filter(isFollower)) {
		if (!isFollowing(actor.follows, bundle, state)) {
			continue;
		}

		const standsIn = roomOfActor(bundle, state, actor.actorId);

		if (standsIn === leftRoomId) {
			const refusal = refusalOf(actor.follows, targetRoomId, bundle, state);

			if (refusal === undefined) {
				arriving.push(actor);
			} else {
				holding.push({ actor, refusal });
			}
		} else if (standsIn === targetRoomId) {
			waiting.push(actor);
		}
	}

	return { arriving, holding, waiting };
};

/**
 * The moved record with every arriving follower standing in the room
 * entered — written as `move_actor` writes it, a delta against the pack.
 *
 * @param actorLocations - The moved record in the doorway
 * @param arriving - The followers coming through
 * @param targetRoomId - The room entered
 * @returns The record after the step
 */
export const withFollowersArrived = (
	actorLocations: RuntimeState['world']['actorLocations'],
	arriving: readonly Follower[],
	targetRoomId: RoomId,
): RuntimeState['world']['actorLocations'] =>
	arriving.reduce((record, actor) => withActorLocation(record, actor, targetRoomId), actorLocations);

/** One body's lines, and the body that speaks them. */
interface CompanyLines {
	readonly actor: ActorDefinition;
	readonly lines: readonly NarrativeLine[];
}

/**
 * Stamp the company's lines in order, each body's under its own
 * `actor_moved:<actorId>` — a refusal and a rejoin are the body's movement
 * as the player sees it — and only for a body the player can see: an unseen
 * follower holds and is found in silence, as an unseen `move_actor` is.
 *
 * @param bundle - The loaded bundle
 * @param state - The state the lines (and the bodies' visibility) are judged against
 * @param cursor - Where the first line lands
 * @param company - The bodies and their lines, in order
 * @returns The stamped entries and the advanced cursor
 */
const stampCompanyLines = (bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor, company: readonly CompanyLines[]): StampedNarratives =>
	company
		.filter(({ actor }) => isActorVisible(bundle, state, actor))
		.reduce<StampedNarratives>(
			(stamped, { actor, lines }) => {
				const next = stampNarrativeLines(bundle, state, stamped.cursor, lines, actorMovedProvenance(actor.actorId));

				return { cursor: next.cursor, entries: [...stamped.entries, ...next.entries]};
			},
			{ cursor, entries: []},
		);

/**
 * The lines of the followers that stop at the threshold — spoken first, as
 * the player goes, judged in the doorway.
 *
 * @param bundle - The loaded bundle
 * @param state - The state in the doorway
 * @param cursor - Where the first line lands
 * @param holding - The followers that hold
 * @returns The stamped entries and the advanced cursor
 */
export const speakThresholdRefusals = (bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor, holding: readonly HoldingFollower[]): StampedNarratives =>
	stampCompanyLines(
		bundle,
		state,
		cursor,
		holding.map(({ actor, refusal }) => ({ actor, lines: refusal.narratives })),
	);

/**
 * The lines of the followers found waiting — spoken last, after the room's
 * page, judged standing in the room. A follower with no `rejoinNarratives`
 * is found in silence.
 *
 * @param bundle - The loaded bundle
 * @param state - The state standing in the room entered
 * @param cursor - Where the first line lands
 * @param waiting - The followers found
 * @returns The stamped entries and the advanced cursor
 */
export const speakRejoins = (bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor, waiting: readonly Follower[]): StampedNarratives =>
	stampCompanyLines(
		bundle,
		state,
		cursor,
		waiting.flatMap((actor) => (actor.follows.rejoinNarratives === undefined ? [] : [{ actor, lines: actor.follows.rejoinNarratives }])),
	);
